/**
 * Client-side entitlement helpers — mirror server get_effective_entitlements.
 * UI gates are advisory; the API remains authoritative.
 */

export function getEntitlements(user) {
  if (!user || user === false) return null;
  return user.entitlements || null;
}

export function isReadOnly(user) {
  if (!user || user === false) return true;
  if (user.role === 'super_admin') return false;
  return Boolean(user.read_only || user.entitlements?.read_only);
}

export function hasFeature(user, feature) {
  if (!user || user === false) return false;
  if (user.role === 'super_admin') return true;
  return Boolean(user.entitlements?.features?.[feature]);
}

export function remainingQuota(user, counter, limitField) {
  const ents = getEntitlements(user);
  if (!ents) return 0;
  if (user?.role === 'super_admin') return Number.POSITIVE_INFINITY;
  const limit = Number(ents.limits?.[limitField] ?? 0);
  if (limit === -1) return Number.POSITIVE_INFINITY;
  const used = Number(ents.usage?.[counter] ?? 0);
  return Math.max(0, limit - used);
}

/**
 * @returns {{ allowed: boolean, reason?: 'upgrade_required'|'limit_reached'|'read_only', label: string, remaining?: number }}
 */
export function featureGate(user, feature) {
  if (!user || user === false) {
    return { allowed: false, reason: 'upgrade_required', label: 'Upgrade Required' };
  }
  if (user.role === 'super_admin') {
    return { allowed: true, label: '' };
  }
  if (isReadOnly(user)) {
    return { allowed: false, reason: 'read_only', label: 'Upgrade Required' };
  }
  if (!hasFeature(user, feature)) {
    return { allowed: false, reason: 'upgrade_required', label: 'Upgrade Required' };
  }
  return { allowed: true, label: '' };
}

/**
 * @returns {{ allowed: boolean, reason?: string, label: string, remaining: number }}
 */
export function quotaGate(user, counter, limitField) {
  if (!user || user === false) {
    return { allowed: false, reason: 'upgrade_required', label: 'Upgrade Required', remaining: 0 };
  }
  if (user.role === 'super_admin') {
    return { allowed: true, label: '', remaining: Number.POSITIVE_INFINITY };
  }
  if (isReadOnly(user)) {
    return { allowed: false, reason: 'read_only', label: 'Upgrade Required', remaining: 0 };
  }
  const limit = Number(user.entitlements?.limits?.[limitField] ?? 0);
  if (limit === 0) {
    return { allowed: false, reason: 'upgrade_required', label: 'Upgrade Required', remaining: 0 };
  }
  const remaining = remainingQuota(user, counter, limitField);
  if (remaining <= 0) {
    return { allowed: false, reason: 'limit_reached', label: 'Monthly Limit Reached', remaining: 0 };
  }
  return { allowed: true, label: '', remaining };
}

export function formatRemaining(remaining) {
  if (remaining === Number.POSITIVE_INFINITY) return 'Unlimited';
  return String(remaining);
}
